import React, { useEffect, useMemo, useState } from 'react';
import type { ScheduleTimeTag } from '../../types/pointItemNote';
import {
  buildHalfHourCustomScheduleTag,
  parseTimeTagLabelToScheduleTag,
} from '../../utils/pointItemScheduleTimeTags';

type Props = {
  open: boolean;
  onClose: () => void;
  tags: ScheduleTimeTag[];
  selected: ScheduleTimeTag | null;
  dayLabel: string;
  /** mon|tue|… — 커스텀 태그 생성 시 사용 */
  dayKey: string;
  /** selected가 없을 때 직접수정 칸을 채울 라벨 (예: [일] 8시) — customPrefill=selected 일 때 */
  labelHint?: string;
  /**
   * 직접수정 칸 프리필
   * - selected: 선택/라벨 정보 반영 (기록 수정 모달)
   * - now: 현재 시각 30분 단위 (페이지 상단 타임태그)
   */
  customPrefill?: 'selected' | 'now';
  onSelect: (tag: ScheduleTimeTag | null) => void;
};

function isValidHHMM(v: string): boolean {
  return /^([01]?\d|2[0-3]):[0-5]\d$/.test(v.trim());
}

const TimeTagSelectModal: React.FC<Props> = ({
  open,
  onClose,
  tags,
  selected,
  dayLabel,
  dayKey,
  labelHint,
  customPrefill = 'selected',
  onSelect,
}) => {
  const [customName, setCustomName] = useState('');
  const [customStart, setCustomStart] = useState('09:00');
  const [customEnd, setCustomEnd] = useState('09:40');

  useEffect(() => {
    if (!open) return;

    if (customPrefill === 'now') {
      const nowTag = buildHalfHourCustomScheduleTag(new Date(), dayKey);
      setCustomName(nowTag.name);
      setCustomStart(nowTag.startTime);
      setCustomEnd(nowTag.endTime);
      return;
    }

    const fromHint =
      !selected && labelHint
        ? parseTimeTagLabelToScheduleTag(labelHint, dayKey)
        : null;
    const source = selected ?? fromHint;
    if (source) {
      setCustomName(source.name || '');
      setCustomStart(source.startTime || '09:00');
      setCustomEnd(source.endTime || '09:40');
      return;
    }
    setCustomName('');
    setCustomStart('09:00');
    setCustomEnd('09:40');
  }, [open, selected, labelHint, dayKey, customPrefill]);

  const canApplyCustom = useMemo(() => {
    const name = customName.trim();
    return Boolean(name) && isValidHHMM(customStart) && isValidHHMM(customEnd);
  }, [customName, customStart, customEnd]);

  if (!open) return null;

  const applyCustom = () => {
    if (!canApplyCustom) return;
    const keepCustomId =
      selected?.id?.startsWith('custom-') && customPrefill === 'selected'
        ? selected.id
        : `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    onSelect({
      id: keepCustomId,
      name: customName.trim(),
      startTime: customStart.trim(),
      endTime: customEnd.trim(),
      dayKey: dayKey || selected?.dayKey || tags[0]?.dayKey || 'mon',
    });
    onClose();
  };

  return (
    <div className="cp-pin-tt-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="cp-pin-tt-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-tt-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-pin-tt-modal-header">
          <h3 id="cp-pin-tt-modal-title">타임 태그 선택</h3>
          <button type="button" className="cp-pin-tt-modal-close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </header>

        <div className="cp-pin-tt-modal-body">
          <p className="cp-pin-tt-modal-day">
            기준 요일: <strong>{dayLabel}</strong>
          </p>

          <button
            type="button"
            className={`cp-pin-tt-none${!selected ? ' is-active' : ''}`}
            onClick={() => {
              onSelect(null);
              onClose();
            }}
          >
            타임 태그 선택 안함
          </button>

          <div className="cp-pin-tt-list">
            {tags.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`cp-pin-tt-item${selected?.id === t.id ? ' is-active' : ''}`}
                onClick={() => {
                  onSelect(t);
                  onClose();
                }}
              >
                <strong>{t.name}</strong>
                <span>
                  {t.startTime} ~ {t.endTime}
                </span>
              </button>
            ))}
            {!tags.length ? (
              <p className="cp-pin-tt-empty">이 요일에 등록된 수업시수가 없습니다. 아래에서 직접 입력할 수 있습니다.</p>
            ) : null}
          </div>

          <section className="cp-pin-tt-custom">
            <h4>직접 수정</h4>
            <label>
              이름
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="예: 1교시"
              />
            </label>
            <div className="cp-pin-tt-custom-times">
              <label>
                시작
                <input
                  type="text"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  placeholder="09:00"
                  inputMode="numeric"
                />
              </label>
              <label>
                종료
                <input
                  type="text"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  placeholder="09:40"
                  inputMode="numeric"
                />
              </label>
            </div>
            <button
              type="button"
              className="cp-pin-tt-custom-apply"
              disabled={!canApplyCustom}
              onClick={applyCustom}
            >
              커스텀 타임 적용
            </button>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TimeTagSelectModal;
